<template>
  <aside
    class="tenant-hierarchy-sidebar"
    :class="{
      'tenant-hierarchy-sidebar--collapsed': collapsed,
      'tenant-hierarchy-sidebar--resizing': resizing,
    }"
    :style="sidebarStyle"
  >
    <div class="tenant-hierarchy-sidebar__header">
      <button
        type="button"
        class="tenant-hierarchy-sidebar__toggle"
        :title="collapsed ? 'メニューを開く' : 'メニューを閉じる'"
        @click="toggleSidebar"
      >
        <i
          class="tenant-hierarchy-sidebar__toggle-icon"
          :class="collapsed ? 'el-icon-d-arrow-right' : 'el-icon-d-arrow-left'"
        ></i>
      </button>
    </div>

    <div v-show="!collapsed" class="tenant-hierarchy-sidebar__body" v-loading="loading">
      <el-tree
        :key="treeRenderKey"
        ref="hierarchyTree"
        class="tenant-hierarchy-tree"
        :data="treeData"
        :props="treeProps"
        node-key="id"
        :indent="8"
        highlight-current
        default-expand-all
        :expand-on-click-node="false"
        @node-click="handleNodeClick"
      >
      <span slot-scope="{ node, data }" class="tenant-hierarchy-tree__node-wrapper">
          <span class="tenant-hierarchy-tree__node">
            <input
              v-if="isEditingNode(data)"
              v-model="editingName"
              type="text"
              maxlength="32"
              class="tenant-hierarchy-tree__input-native"
              @click.stop
              @mousedown.stop
            />
            <template v-else>
              <div
                class="tenant-hierarchy-tree__label-wrap"
                @click.stop="handleAntNameClick(node, data)"
              >
                <el-tooltip
                  :content="node.label"
                  placement="top"
                  effect="light"
                  :open-delay="200"
                  popper-class="ant-menu-name-tooltip"
                >
                  <span
                    class="tenant-hierarchy-tree__label"
                    :class="{ 'tenant-hierarchy-tree__label--root': !isEditableAnt(data) }"
                  >{{ node.label }}</span>
                </el-tooltip>
              </div>
              <button
                v-if="isEditableAnt(data)"
                type="button"
                class="tenant-hierarchy-tree__edit-btn"
                title="名前を編集"
                @mousedown.stop
                @click.stop="startEdit(data, node)"
              >
                <i class="el-icon-edit tenant-hierarchy-tree__edit-icon"></i>
              </button>
            </template>
          </span>
        </span>
      </el-tree>
    </div>

    <div v-show="!collapsed" class="tenant-hierarchy-sidebar__footer">
      <el-button
        type="primary"
        class="tenant-hierarchy-sidebar__export-btn"
        :loading="exporting"
        @click="exportHierarchyCsv"
      >
        階層CSV出力
      </el-button>
    </div>

    <div
      v-show="!collapsed"
      class="tenant-hierarchy-sidebar__resizer"
      title="ドラッグして幅を変更"
      @mousedown.prevent="startResize"
      @dblclick="resetWidth"
    ></div>
  </aside>
</template>

<script>
import { mapGetters } from "vuex";
import {
  EXPORT_ANT_MENU_CSV,
  RENAME_ANT_MENU,
  SET_SELECTED_ANT_MENU,
  UPDATE_ANT_MENU_STATE,
} from "@/core/services/store/antMenu.module";
import {
  isAntMenuOpen,
  toHierarchyMenuOpen,
} from "@/core/services/antMenu.service";

export default {
  name: "AntMenuSidebar",
  data() {
    return {
      collapsed: true,
      exporting: false,
      sidebarWidth: 260,
      minWidth: 200,
      maxWidth: 560,
      defaultWidth: 260,
      resizing: false,
      resizeStorageKey: "tenantSidebarWidth",
      treeProps: {
        label: "label",
        children: "children",
        isLeaf: "isLeaf",
      },
      skipBlurAfterEnter: false,
      editingNodeId: null,
      editingName: "",
      editingContext: null,
      treeRenderKey: 0,
      editingInputEl: null,
      savingEdit: false,
    };
  },
  computed: {
    ...mapGetters([
      "antMenuTree",
      "antMenuOpen",
      "antMenuLoading",
      "selectedAntMenu",
      "currentUser",
    ]),
    currentUserTenantCode() {
      const user = this.currentUser || {};
      const role = user.role || {};
      return role.tenant_code ?? user.tenant_code ?? null;
    },
    treeData() {
      return this.antMenuTree || [];
    },
    loading() {
      return this.antMenuLoading;
    },
    sidebarStyle() {
      if (this.collapsed) {
        return {};
      }
      return { width: this.sidebarWidth + "px" };
    },
  },
  created() {
    this.initSidebarWidth();
  },
  beforeDestroy() {
    this.unbindEditingInputEvents();
    this.stopResize();
  },
  watch: {
    antMenuOpen: {
      immediate: true,
      handler(value) {
        this.applyMenuOpenState(value);
      },
    },
    selectedAntMenu: {
      immediate: true,
      handler(ant) {
        if (!ant || !this.$refs.hierarchyTree) {
          return;
        }
        this.$nextTick(() => {
          this.$refs.hierarchyTree.setCurrentKey(ant.id);
        });
      },
    },
  },
  methods: {
    initSidebarWidth() {
      const stored = parseInt(
        window.localStorage.getItem(this.resizeStorageKey),
        10
      );
      if (!Number.isNaN(stored)) {
        this.sidebarWidth = this.clampWidth(stored);
      }
      this.applyWidthVar();
    },
    clampWidth(width) {
      return Math.min(this.maxWidth, Math.max(this.minWidth, width));
    },
    applyWidthVar() {
      document.documentElement.style.setProperty(
        "--tenant-sidebar-width",
        this.sidebarWidth + "px"
      );
    },
    startResize() {
      if (this.collapsed) {
        return;
      }
      this.resizing = true;
      document.body.classList.add("tenant-sidebar-resizing");
      document.addEventListener("mousemove", this.onResizing);
      document.addEventListener("mouseup", this.stopResize);
    },
    onResizing(event) {
      if (!this.resizing) {
        return;
      }
      this.sidebarWidth = this.clampWidth(event.clientX);
      this.applyWidthVar();
    },
    stopResize() {
      if (!this.resizing) {
        return;
      }
      this.resizing = false;
      document.body.classList.remove("tenant-sidebar-resizing");
      document.removeEventListener("mousemove", this.onResizing);
      document.removeEventListener("mouseup", this.stopResize);
      window.localStorage.setItem(this.resizeStorageKey, this.sidebarWidth);
    },
    resetWidth() {
      this.sidebarWidth = this.defaultWidth;
      this.applyWidthVar();
      window.localStorage.setItem(this.resizeStorageKey, this.sidebarWidth);
    },
    async toggleSidebar() {
      const previousCollapsed = this.collapsed;
      const nextCollapsed = !this.collapsed;

      this.applyMenuCollapsedState(nextCollapsed);

      try {
        await this.$store.dispatch(
          UPDATE_ANT_MENU_STATE,
          toHierarchyMenuOpen(nextCollapsed)
        );
      } catch (error) {
        this.applyMenuCollapsedState(previousCollapsed);
        this.notifyError(error.message || error);
      }
    },
    applyMenuCollapsedState(collapsed) {
      this.collapsed = collapsed;
      this.$emit("toggle", collapsed);
    },
    applyMenuOpenState(hierarchy_menu_open) {
      this.applyMenuCollapsedState(!isAntMenuOpen(hierarchy_menu_open));
    },
    handleNodeClick(data) {
      this.closeAnyEditing();
      if (!data) {
        this.$store.dispatch(SET_SELECTED_ANT_MENU, null);
        return;
      }
      this.$store.dispatch(SET_SELECTED_ANT_MENU, data);
    },
    hasChildNodes(node, data) {
      if (node.childNodes && node.childNodes.length > 0) {
        return true;
      }
      return !!(data.children && data.children.length > 0);
    },
    toggleNodeExpand(node) {
      if (node.expanded) {
        node.collapse();
      } else {
        node.expand();
      }
    },
    handleAntNameClick(node, data) {
      if (this.hasChildNodes(node, data)) {
        this.toggleNodeExpand(node);
      }
      this.handleNodeClick(data);
    },
    isSelectedNode(data) {
      if (!this.selectedAntMenu || !data) {
        return false;
      }
      return this.selectedAntMenu.id === data.id;
    },
    isEditableAnt(data) {
      if (!data || (data.depth ?? 0) <= 0) {
        return false;
      }
      if (
        this.currentUserTenantCode &&
        data.tenant_code === this.currentUserTenantCode
      ) {
        return false;
      }
      return true;
    },
    isEditingNode(data) {
      return !!data && this.editingNodeId === data.id;
    },
    closeAnyEditing() {
      if (!this.editingNodeId) {
        return;
      }

      const { data, originalName } = this.editingContext || {};
      if (data && originalName !== undefined) {
        this.applyAntName(data, originalName);
      }
      this.resetEditState();
    },
    startEdit(data, node) {
      if (!this.isEditableAnt(data)) {
        return;
      }

      this.closeAnyEditing();
      this.editingNodeId = data.id;
      this.editingName = data.tenant_name || data.label || "";
      this.editingContext = {
        data,
        node,
        originalName: data.tenant_name || data.label || "",
      };
      this.treeRenderKey += 1;

      this.$nextTick(() => {
        this.bindEditingInputEvents();
      });
    },
    bindEditingInputEvents() {
      this.unbindEditingInputEvents();
      const input = this.$el.querySelector(".tenant-hierarchy-tree__input-native");
      if (!input) {
        return;
      }

      this.editingInputEl = input;
      input.addEventListener("keydown", this.handleEditingKeydown);
      input.addEventListener("blur", this.handleEditingBlur);
      input.focus();
      input.select();
    },
    unbindEditingInputEvents() {
      if (!this.editingInputEl) {
        return;
      }

      this.editingInputEl.removeEventListener("keydown", this.handleEditingKeydown);
      this.editingInputEl.removeEventListener("blur", this.handleEditingBlur);
      this.editingInputEl = null;
    },
    handleEditingKeydown(event) {
      if (event.key !== "Enter") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.skipBlurAfterEnter = true;
      this.finishEditOnEnter();
    },
    handleEditingBlur() {
      if (this.skipBlurAfterEnter) {
        this.skipBlurAfterEnter = false;
        return;
      }

      this.finishEditOnBlur();
    },
    finishEditOnBlur() {
      this.commitEdit();
    },
    finishEditOnEnter() {
      this.commitEdit({ showSuccess: true });
    },
    applyAntName(data, tenantName) {
      if (!data) {
        return;
      }
      this.$set(data, "label", tenantName);
      this.$set(data, "tenant_name", tenantName);
    },
    resetEditState() {
      this.unbindEditingInputEvents();
      this.editingNodeId = null;
      this.editingName = "";
      this.editingContext = null;
      this.savingEdit = false;
      this.treeRenderKey += 1;
    },
    async commitEdit(options = {}) {
      if (!this.editingNodeId || this.savingEdit) {
        return;
      }

      const { data, originalName } = this.editingContext || {};
      const trimmedName = (this.editingName || "").trim();

      if (!trimmedName || !data) {
        this.resetEditState();
        return;
      }

      if (trimmedName === originalName) {
        this.resetEditState();
        return;
      }

      if (!data.tenant_code) {
        this.notifyError("tenant_code is required");
        this.resetEditState();
        return;
      }

      this.savingEdit = true;
      try {
        const result = await this.$store.dispatch(RENAME_ANT_MENU, {
          tenant_code: data.tenant_code,
          tenant_name: trimmedName,
        });
        const updatedName = result?.tenant_name || trimmedName;
        this.applyAntName(data, updatedName);

        if (options.showSuccess) {
          this.notifySuccess(this.$t("UPDATE_SUCCESS"));
        }
      } catch (error) {
        this.applyAntName(data, originalName);
        this.notifyError(error.message || error);
      } finally {
        this.resetEditState();
      }
    },
    async exportHierarchyCsv() {
      this.exporting = true;
      try {
        const data = await this.$store.dispatch(EXPORT_ANT_MENU_CSV);
        const content = data.startsWith('\uFEFF') ? data : '\uFEFF' + data;
        const blob = new Blob([content], { type: 'text/csv;charset=UTF-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const pad = n => String(n).padStart(2, '0');
        const now = new Date();
        const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        link.download = `SC018_hierarchy_${ts}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        this.notifyError(error.message || error);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tenant-hierarchy-sidebar {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 95;
  display: flex;
  flex-direction: column;
  width: 260px;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  background: #f3f6f9;
  border-right: 1px solid #e4e6ef;
  transition: width 0.2s ease;
  &--collapsed {
    width: 44px;
  }
  &--resizing {
    transition: none;
    user-select: none;
  }

  &__resizer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 6px;
    cursor: col-resize;
    z-index: 96;
    background: transparent;
    transition: background-color 0.15s ease;

    &:hover,
    &:active {
      background: #009ef7;
    }
  }

  &__header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-shrink: 0;
    min-height: 55px;
    padding: 8px;
    border-bottom: 1px solid #e4e6ef;
    background: #fff;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;

    &:hover {
      background: #eef6ff;

      .tenant-hierarchy-sidebar__toggle-icon {
        color: #002244;
      }
    }
  }

  &__toggle-icon {
    font-size: 20px;
    color: #003366;
    -webkit-text-stroke: 0.35px #003366;
  }

  &__body {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 12px 8px;
  }

  &__footer {
    flex-shrink: 0;
    padding: 12px;
    background: #f3f6f9;
    border-top: 1px solid #e4e6ef;
  }

  &__export-btn {
    width: 100%;
  }
}

.tenant-hierarchy-tree {
  background: transparent;

  ::v-deep .el-tree-node__content {
    height: auto;
    min-height: 30px;
    padding-top: 2px;
    padding-bottom: 2px;
    border-radius: 4px;
    overflow: hidden;
    align-items: center;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #eef6ff !important;
    }
  }

  ::v-deep .el-tree-node__expand-icon {
    margin-top: 0;
  }

  ::v-deep .el-tree-node.is-current > .el-tree-node__content {
    background-color: #e1effd;
  }

  ::v-deep .el-tree-node.is-current > .el-tree-node__content:hover {
    background-color: #eef6ff !important;
  }

  ::v-deep .el-tree-node__expand-icon {
    padding: 4px;
  }

  &__node-wrapper {
    flex: 1;
    min-width: 0;
    display: block;
    overflow: hidden;
  }

  &__label-wrap {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    cursor: pointer;
  }

  ::v-deep .el-tooltip {
    display: block;
    max-width: 100%;
    overflow: hidden;
  }

  &__node {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    min-width: 0;
    padding: 3px 4px 3px 0;
    font-size: 15px;
    cursor: pointer;
  }

  &__label {
    display: block;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.35;
    font-size: 15px;
    font-weight: 400;

    &--root {
      font-size: 16px;
      font-weight: 600;
    }
  }

  &__input-native {
    flex: 1;
    min-width: 0;
    width: 100%;
    height: 28px;
    padding: 0 8px;
    font-size: 15px;
    line-height: 28px;
    color: #606266;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    outline: none;
    background: #fff;

    &:focus {
      border-color: #409eff;
    }
  }

  &__edit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #dbeafe;
    }
  }

  &__edit-icon {
    font-size: 15px;
    color: #0066cc;
  }

  &__pin {
    flex-shrink: 0;
    margin-left: 6px;
    color: #009ef7;
    font-size: 14px;
  }
}

@media (max-width: 991px) {
  .tenant-hierarchy-sidebar {
    display: none;
  }
}
</style>

<style lang="scss">
.ant-menu-name-tooltip {
  max-width: 280px;
  padding: 6px 10px;
  font-size: 13px;
  line-height: 1.4;
  color: #181c32;
  word-break: break-word;
  border: 1px solid #e4e6ef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 4px !important;
}
</style>
